import React, { createContext, useState, useEffect, useContext } from 'react';
import { useAuth } from './AuthContext';
import { 
  getPredictionCycle, 
  initializePredictionCycle 
} from '../services/predictionService';

const PredictionContext = createContext();

export const usePrediction = () => {
  return useContext(PredictionContext);
};

export const PredictionProvider = ({ children }) => {
  const { currentUser, isDemoMode } = useAuth();
  const [predictionCycle, setPredictionCycle] = useState({
    count: 0,
    correctCount: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPredictionCycle = async () => {
      if (!currentUser) {
        setPredictionCycle({ count: 0, correctCount: 0 });
        return;
      }

      // Use demo data
      if (isDemoMode || currentUser.isDemo) {
        setPredictionCycle({ 
          count: 8, 
          correctCount: 5 
        });
        return;
      }

      try {
        // Get prediction cycle data
        const cycleData = await getPredictionCycle(currentUser.uid);
        setPredictionCycle(cycleData);
      } catch (error) {
        console.error("Error fetching prediction cycle data:", error);
      }
    };

    fetchPredictionCycle();
  }, [currentUser, isDemoMode]);

  // Calculate percentage to show progress
  const getProgressPercentage = () => {
    const { count, correctCount } = predictionCycle;
    
    // Progress towards completing 20 predictions
    const cycleProgress = (count / 20) * 100;
    
    // Progress towards achieving 12 correct predictions
    const correctProgress = (correctCount / 12) * 100;
    
    return {
      cycleProgress: Math.min(cycleProgress, 100),
      correctProgress: Math.min(correctProgress, 100)
    };
  };

  // Calculate remaining predictions needed for cycle completion
  const getRemainingPredictions = () => {
    const { count, correctCount } = predictionCycle;
    
    const totalRemaining = 20 - count;
    const correctRemaining = 12 - correctCount;
    
    return {
      totalRemaining: Math.max(totalRemaining, 0),
      correctRemaining: Math.max(correctRemaining, 0)
    };
  };
  
  // Check if user qualifies for bonus
  const isQualifiedForBonus = () => {
    const { correctCount } = predictionCycle;
    return correctCount >= 12;
  };
  
  // Check if cycle is complete
  const isCycleComplete = () => {
    const { count } = predictionCycle;
    return count >= 20;
  };

  // Format prediction cycle data for display
  const getFormattedCycleData = () => {
    const { count, correctCount } = predictionCycle;
    const { totalRemaining, correctRemaining } = getRemainingPredictions();
    const { cycleProgress, correctProgress } = getProgressPercentage();
    
    return {
      total: {
        completed: count,
        remaining: totalRemaining,
        target: 20,
        progress: cycleProgress
      },
      correct: {
        completed: correctCount,
        remaining: correctRemaining,
        target: 12,
        progress: correctProgress
      },
      isComplete: isCycleComplete(),
      isQualifiedForBonus: isQualifiedForBonus()
    };
  };

  const value = {
    predictionCycle,
    getFormattedCycleData,
    getProgressPercentage,
    getRemainingPredictions,
    isQualifiedForBonus,
    isCycleComplete
  };

  return (
    <PredictionContext.Provider value={value}>
      {children}
    </PredictionContext.Provider>
  );
}; 